import { Button, DetectionZone, Device, Icons, LaneLine, PerspectiveCanvas, ScreenLine, useImageCoordTranslator } from "@app/shared";
import type { PerspectiveShape } from "@app/shared";
import React, { useCallback, useState } from "react";
import { toastError, toastSuccess, toastWarning } from "../../../helpers";
import { DeviceSnapshotState, useDeviceSnapshot } from "../../../hooks/useDeviceSnapshot";

type DeviceCameraViewProps = {
    device: Device | undefined;
    /** Facility-frame shapes (meters) — same instances the map view renders */
    shapes: PerspectiveShape[];
    inProgressShapeId?: string;
    editable: boolean;
    selectedShapeIds: ReadonlySet<string>;
    showLabels: { detectionZones: boolean; screenLines: boolean; laneLines: boolean };
    onShapeChanged: (shape: PerspectiveShape, remove: boolean) => void;
    onShapeClick: (shapeId: string) => void;
    onBackgroundClick: () => void;
    /**
     * Supplies the snapshot from somewhere other than CoreApi. The default resolution goes through
     * devicePhotoUrl and the target-photo client, neither of which exists on the firmware's own API —
     * so the webconfig passes its layout-image endpoint here instead of 404ing against itself.
     */
    snapshotOverride?: DeviceSnapshotState;
};

const isTempId = (id: string | undefined) => !!id && /^\d+$/.test(id);

/**
 * Camera-perspective view for the layout designer: renders the facility's shapes over the
 * device's camera snapshot and lets the user draw/edit them from the device's point of view.
 */
export const DeviceCameraView: React.FC<DeviceCameraViewProps> = ({ device, shapes, inProgressShapeId, editable, selectedShapeIds, showLabels, onShapeChanged, onShapeClick, onBackgroundClick, snapshotOverride }) => {
    // Pass undefined when overridden so the CoreApi lookups never fire (they 404 on the firmware).
    const resolvedSnapshot = useDeviceSnapshot(snapshotOverride ? undefined : device);
    const snapshot = snapshotOverride ?? resolvedSnapshot;
    const [imageDimensions, setImageDimensions] = useState({ width: 1920, height: 1080 });
    const [imageLoadError, setImageLoadError] = useState(false);

    const translator = useImageCoordTranslator(device, imageDimensions.width, imageDimensions.height);

    // New shapes drawn from this camera's perspective are anchored to the device;
    // existing (map-anchored) shapes edited here keep their anchor.
    const handleShapeChanged = useCallback(
        (shape: PerspectiveShape, remove: boolean) => {
            if (!remove && device && isTempId(shape.id) && !shape.deviceId && (shape.$type === DetectionZone.$type || shape.$type === ScreenLine.$type || shape.$type === LaneLine.$type)) {
                shape.deviceId = device.id;
            }
            onShapeChanged(shape, remove);
        },
        [device, onShapeChanged],
    );

    const handleRequestNewPhoto = useCallback(async () => {
        let updated: boolean;
        try {
            updated = await snapshot.requestNewPhoto();
        } catch (e) {
            toastError("Unable to request a photo", e instanceof Error ? e.message : String(e));
            return;
        }
        if (updated) {
            setImageLoadError(false);
            toastSuccess("New camera photo received.");
        } else {
            toastWarning("No photo yet", "The device hasn't uploaded a new photo. Make sure it is online and try again.", 8000);
        }
    }, [snapshot]);

    const requestPhotoButton = (
        <Button className="flex items-center gap-2 ml-0" onClick={handleRequestNewPhoto} disabled={!device || !snapshot.canRequestNewPhoto || snapshot.isRequesting} title={snapshot.canRequestNewPhoto ? "Ask the device camera for a fresh photo (arrives on its next check-in)" : "This device's firmware does not support photo requests"}>
            {snapshot.isRequesting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" /> : <Icons.Photo size={18} />}
            {snapshot.isRequesting ? "Waiting for device…" : "Request new photo"}
        </Button>
    );

    if (!device) {
        return (
            <div className="w-full rounded bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center py-16 px-8">
                <Icons.Video className="w-12 h-12 text-gray-400 mb-3" />
                <p className="text-gray-500">This facility has no devices to view from.</p>
            </div>
        );
    }

    if (snapshot.isLoading) {
        return (
            <div className="w-full rounded bg-gray-100 flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                <span className="ml-3 text-gray-600">Loading camera snapshot…</span>
            </div>
        );
    }

    if (!snapshot.url || imageLoadError) {
        return (
            <div className="w-full rounded bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center py-16 px-8">
                <Icons.Photo className="w-16 h-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No camera snapshot</h3>
                <p className="text-gray-500 text-center mb-6 max-w-md">There is no photo from this device's camera yet. Request one to draw zones, screen lines, and lane lines from the camera's perspective.</p>
                {requestPhotoButton}
            </div>
        );
    }

    // The canvas image is width-driven (w-full), so bounding the width by
    // viewport-height x aspect is what keeps the snapshot inside the viewport instead of
    // running past the fold and pushing the layout controls out of reach.
    const imageAspect = imageDimensions.height > 0 ? imageDimensions.width / imageDimensions.height : 4 / 3;

    return (
        <div className="relative mx-auto xl:max-w-[calc((100vh_-_16rem)*var(--camera-aspect))]" style={{ ["--camera-aspect" as string]: `${imageAspect}` } as React.CSSProperties}>
            <PerspectiveCanvas
                imageUrl={snapshot.url}
                imageWidth={imageDimensions.width}
                imageHeight={imageDimensions.height}
                translator={translator}
                shapes={shapes}
                inProgressShapeId={inProgressShapeId}
                editable={editable}
                selectedShapeIds={selectedShapeIds}
                showLabels={showLabels}
                onShapeChanged={handleShapeChanged}
                onShapeClick={onShapeClick}
                onBackgroundClick={onBackgroundClick}
                onImageLoad={(dimensions) => {
                    setImageLoadError(false);
                    if (dimensions.width > 0 && dimensions.height > 0) {
                        setImageDimensions((current) => (current.width === dimensions.width && current.height === dimensions.height ? current : dimensions));
                    }
                }}
                onImageError={() => setImageLoadError(true)}
            />
            <div className="absolute top-2 right-2">{requestPhotoButton}</div>
        </div>
    );
};
